import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Integrations from "../components/Integrations";
import {
  ShieldCheck, ScrollText, Settings2, Youtube, BarChart3, UserCog, Bell, LayoutGrid,
  Plus, Trash2, Search, CreditCard,
} from "lucide-react";
import api from "../lib/api";
import ConsoleShell, { StatCard } from "../components/ConsoleShell";
import { Spinner, EmptyState, StatusBadge, formatViews } from "../components/states";
import { toast } from "sonner";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "admins", label: "Admins", icon: UserCog },
  { key: "memberships", label: "Memberships", icon: CreditCard },
  { key: "audit", label: "Audit Log", icon: ScrollText },
  { key: "integrations", label: "Integrations", icon: Youtube },
  { key: "settings", label: "Settings", icon: Settings2 },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "notifications", label: "Alerts", icon: Bell },
];

export default function SuperConsole() {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "overview";
  const setActive = (k) => setParams((p) => { const n = new URLSearchParams(p); if (k === "overview") n.delete("tab"); else n.set("tab", k); return n; });
  return (
    <ConsoleShell title={label(active)} subtitle="SYSTEM CONTROL CENTER" nav={NAV} active={active} onNav={setActive} fallback="/console" crumbs={[{ label: "Dashboard", to: "/dashboard" }, { label: "Admin", to: "/console" }, { label: "Super Admin", to: "/console/super" }, ...(active !== "overview" ? [{ label: label(active) }] : [])]}>
      {active === "overview" && <Overview go={setActive} />}
      {active === "admins" && <Admins />}
      {active === "memberships" && <Memberships />}
      {active === "audit" && <AuditLog />}
      {active === "integrations" && <Integrations superAdmin />}
      {active === "settings" && <Settings section="all" />}
      {active === "analytics" && <Analytics />}
      {active === "notifications" && <Alerts />}
    </ConsoleShell>
  );
}
const label = (k) => ({ overview: "Overview", admins: "Admin Accounts", memberships: "Memberships & Payments", audit: "Audit Log", integrations: "Integrations", settings: "Platform Settings", analytics: "Analytics", notifications: "System Alerts" }[k]);

function Memberships() {
  const [s, setS] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [pay, setPay] = useState(null);
  const load = useCallback(() => {
    api.get("/superadmin/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/payments/admin/all").then((r) => setPay(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!s || !cfg || !pay) return <Spinner />;
  const tiers = s.membership?.tiers || cfg.tiers;
  const upd = (i, k, v) => { const t = tiers.map((x, j) => (j === i ? { ...x, [k]: v } : x)); setS({ ...s, membership: { ...s.membership, tiers: t } }); };
  const save = async () => { try { await api.put("/superadmin/settings", { ...s, membership: { ...s.membership, tiers } }); toast.success("Tier pricing saved"); load(); } catch { toast.error("Failed"); } };
  const inp = "w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8552A]";
  const Prov = ({ name, on }) => <span data-testid={`provider-${name}`} className={`mono-label rounded-full px-3 py-1 border ${on ? "border-[#10B981] text-[#10B981]" : "border-[#27272A] text-[#71717A]"}`}>{name.toUpperCase()} {on ? "LIVE" : "NOT CONFIGURED"}</span>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Revenue (₹)" value={(pay.revenue / 100).toLocaleString("en-IN")} icon={CreditCard} testid="s-revenue" />
        <StatCard label="Paid" value={pay.items.filter((p) => p.status === "paid").length} icon={ShieldCheck} testid="s-paid" />
        <StatCard label="Plus / Prime / Premium" value={`${pay.by_tier.plus || 0} / ${pay.by_tier.prime || 0} / ${pay.by_tier.premium || 0}`} icon={UserCog} testid="s-by-tier" />
        <StatCard label="Failed" value={pay.items.filter((p) => p.status === "failed").length} icon={Bell} testid="s-failed" />
      </div>
      <div className="card-copper rounded-xl p-5 flex flex-wrap items-center gap-3">
        <span className="mono-label text-[#71717A]">PROVIDERS</span>
        <Prov name="razorpay" on={cfg.razorpay.enabled} /><Prov name="paypal" on={cfg.paypal.enabled} />
        {!cfg.configured && <span data-testid="payments-not-configured" className="mono-label text-[#FF9F1C]">PAYMENTS NOT CONFIGURED — set RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET or PAYPAL_CLIENT_ID / PAYPAL_SECRET in Secrets</span>}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        {tiers.map((t, i) => (
          <div key={t.key} data-testid={`tier-editor-${t.key}`} className="card-copper rounded-xl p-5 space-y-3">
            <h3 className="font-display font-bold uppercase text-[#E8552A]">{t.key}</h3>
            <div><label className="mono-label text-[#71717A]">Name</label><input data-testid={`tier-${t.key}-name`} value={t.name} onChange={(e) => upd(i, "name", e.target.value)} className={inp} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="mono-label text-[#71717A]">₹ / month</label><input data-testid={`tier-${t.key}-monthly`} type="number" value={t.price_monthly} onChange={(e) => upd(i, "price_monthly", Number(e.target.value))} className={inp} /></div>
              <div><label className="mono-label text-[#71717A]">₹ / year</label><input data-testid={`tier-${t.key}-yearly`} type="number" value={t.price_yearly} onChange={(e) => upd(i, "price_yearly", Number(e.target.value))} className={inp} /></div>
            </div>
            <div><label className="mono-label text-[#71717A]">Shop discount %</label><input data-testid={`tier-${t.key}-discount`} type="number" value={t.shop_discount} onChange={(e) => upd(i, "shop_discount", Number(e.target.value))} className={inp} /></div>
            <div><label className="mono-label text-[#71717A]">Perks (one per line)</label><textarea data-testid={`tier-${t.key}-perks`} rows={5} value={t.perks.join("\n")} onChange={(e) => upd(i, "perks", e.target.value.split("\n").filter(Boolean))} className={inp} /></div>
          </div>
        ))}
      </div>
      <button data-testid="save-tiers" onClick={save} className="copper-btn rounded-lg px-6 py-3 mono-label">SAVE TIER PRICING</button>
      <div className="card-copper rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#27272A] font-display font-bold uppercase">Recent payments</div>
        {pay.items.length === 0 ? <EmptyState title="No payments yet" /> : pay.items.slice(0, 30).map((p) => (
          <div key={p.id} data-testid={`payment-${p.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#27272A]/60 text-sm">
            <span className="flex-1 min-w-[160px] truncate">{p.user_email}</span>
            <span className="mono-label text-[#E8552A]">{p.tier_name} · {p.period}</span>
            <span className="font-display font-bold">₹{(p.amount / 100).toLocaleString("en-IN")}</span>
            <span className="mono-label text-[#71717A]">{p.provider}</span>
            <StatusBadge status={p.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Overview({ go }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/superadmin/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <Spinner />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Total Users" value={a.total_users} icon={UserCog} testid="s-users" />
        <StatCard label="Total DJs" value={a.total_djs} icon={UserCog} testid="s-djs" />
        <StatCard label="Members" value={a.members} icon={ShieldCheck} testid="s-members" />
        <StatCard label="Recordings" value={a.total_recordings} icon={LayoutGrid} testid="s-recs" />
        <StatCard label="Published" value={a.published} icon={LayoutGrid} testid="s-pub" />
        <StatCard label="Total Views" value={formatViews(a.total_views)} icon={BarChart3} testid="s-views" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Admins", "admins"], ["Audit Log", "audit"], ["Integrations", "integrations"], ["Settings", "settings"]].map(([t, k]) => (
          <button key={k} onClick={() => go(k)} className="card-copper rounded-xl p-5 text-left"><p className="font-display font-bold uppercase text-sm">{t}</p><p className="mono-label text-[#E8552A] mt-2">OPEN →</p></button>
        ))}
      </div>
    </div>
  );
}

function Admins() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "ADMIN" });
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/superadmin/admins").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await api.post("/superadmin/admins", form); toast.success("Admin created"); setOpen(false); setForm({ name: "", email: "", password: "", role: "ADMIN" }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const setStatus = async (id, status) => { try { await api.patch(`/superadmin/admins/${id}`, { status }); toast.success("Updated"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { await api.delete(`/superadmin/admins/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const inp = "w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#E8552A]";
  return (
    <div>
      <button data-testid="add-admin-btn" onClick={() => setOpen(!open)} className="copper-btn rounded-full px-4 py-2 mono-label mb-4 flex items-center gap-1"><Plus size={14} /> NEW ADMIN</button>
      {open && (
        <div className="card-copper rounded-xl p-5 mb-5 space-y-3 max-w-lg">
          <input data-testid="admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={inp} />
          <input data-testid="admin-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inp} />
          <input data-testid="admin-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className={inp} />
          <select data-testid="admin-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp}><option value="ADMIN">ADMIN</option><option value="SUPER_ADMIN">SUPER_ADMIN</option></select>
          <button data-testid="save-admin" onClick={create} className="copper-btn rounded-lg px-5 py-2.5 mono-label">CREATE</button>
        </div>
      )}
      {!items ? <Spinner /> : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} data-testid={`admin-row-${a.id}`} className="card-copper rounded-xl p-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#E8552A]/20 flex items-center justify-center font-display font-bold text-[#E8552A]">{a.name?.[0]}</span>
              <div className="flex-1"><p className="font-display font-semibold">{a.name}</p><p className="mono-label text-[#71717A]">{a.email}</p></div>
              <StatusBadge status={a.role === "SUPER_ADMIN" ? "approved" : "published"} />
              <span className="mono-label text-[#A1A1AA]">{a.role}</span>
              <StatusBadge status={a.status || "active"} />
              <button onClick={() => setStatus(a.id, a.status === "suspended" ? "active" : "suspended")} className="border border-[#27272A] rounded-lg px-3 py-1.5 mono-label hover:border-[#E8552A]">{a.status === "suspended" ? "ENABLE" : "SUSPEND"}</button>
              {a.role === "ADMIN" && <button data-testid={`delete-admin-${a.id}`} onClick={() => del(a.id)} className="border border-[#ef4444]/40 text-[#ef4444] rounded-lg px-3 py-1.5 mono-label"><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditLog() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => { setItems(null); api.get(`/superadmin/audit${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setItems(r.data)); }, [q]);
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div>
      <div className="flex items-center gap-2 bg-[#121216] border border-[#27272A] rounded-lg px-3 max-w-md mb-5 focus-within:border-[#E8552A]">
        <Search size={15} className="text-[#71717A]" />
        <input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search action / actor / object…" className="bg-transparent py-2.5 text-sm outline-none flex-1" />
      </div>
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="No audit entries" icon={ScrollText} /> : (
        <div className="overflow-x-auto card-copper rounded-xl">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#27272A] text-left mono-label text-[#71717A]">
              <th className="p-3">TIME</th><th className="p-3">ACTOR</th><th className="p-3">ROLE</th><th className="p-3">ACTION</th><th className="p-3">OBJECT</th><th className="p-3">STATUS</th>
            </tr></thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} data-testid={`audit-${e.id}`} className="border-b border-[#1A1A22]">
                  <td className="p-3 mono-label text-[#71717A] whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="p-3">{e.actor_name}</td>
                  <td className="p-3 mono-label text-[#A1A1AA]">{e.role}</td>
                  <td className="p-3 text-[#E8552A] font-mono text-xs">{e.action}</td>
                  <td className="p-3 mono-label text-[#71717A]">{e.object_type} {e.object_id ? e.object_id.slice(0, 10) : ""}</td>
                  <td className="p-3"><StatusBadge status={e.status === "success" ? "approved" : "rejected"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Settings({ section }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/superadmin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/superadmin/settings", s); toast.success("Settings saved"); } catch { toast.error("Failed"); } };
  if (!s) return <Spinner />;
  const upd = (grp, k, v) => setS({ ...s, [grp]: { ...s[grp], [k]: v } });
  const inp = "w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#E8552A]";
  const Field = ({ grp, k, label: lb, type = "text" }) => (
    <div><label className="mono-label text-[#71717A]">{lb}</label>
      {type === "bool" ? (
        <button data-testid={`toggle-${grp}-${k}`} onClick={() => upd(grp, k, !s[grp]?.[k])} className={`block mt-1 mono-label rounded-full px-4 py-1.5 border ${s[grp]?.[k] ? "border-[#10B981] text-[#10B981]" : "border-[#27272A] text-[#71717A]"}`}>{s[grp]?.[k] ? "ENABLED" : "DISABLED"}</button>
      ) : (
        <input data-testid={`set-${grp}-${k}`} value={s[grp]?.[k] ?? ""} onChange={(e) => upd(grp, k, e.target.value)} className={`${inp} mt-1`} />
      )}
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      {(section === "youtube" || section === "all") && (
        <div className="card-copper rounded-xl p-6 space-y-4">
          <h3 className="font-display font-bold uppercase flex items-center gap-2"><Youtube size={18} className="text-[#E8552A]" /> YouTube Publishing Defaults</h3>
          <p className="mono-label text-[#71717A]">CHANNEL CONNECTION IS MANAGED UNDER INTEGRATIONS</p>
          <Field grp="youtube" k="default_playlist" label="Default playlist" />
          <Field grp="youtube" k="default_visibility" label="Default visibility" />
          <Field grp="youtube" k="title_template" label="Title template" />
          <Field grp="youtube" k="description_template" label="Description template" />
          <Field grp="youtube" k="retry_attempts" label="Retry attempts" />
        </div>
      )}
      {section === "all" && (
        <>
          <div className="card-copper rounded-xl p-6 space-y-4"><h3 className="font-display font-bold uppercase">Branding</h3><Field grp="branding" k="name" label="Platform name" /><Field grp="branding" k="city" label="City" /><Field grp="branding" k="tagline" label="Tagline" /></div>
          <div className="card-copper rounded-xl p-6 space-y-4"><h3 className="font-display font-bold uppercase">Streaming</h3><Field grp="streaming" k="provider" label="Provider" /><Field grp="streaming" k="quality" label="Quality" /><Field grp="streaming" k="stream_key_set" label="Stream key set" type="bool" /></div>
          <div className="card-copper rounded-xl p-6 space-y-4"><h3 className="font-display font-bold uppercase">Storage</h3><Field grp="storage" k="provider" label="Provider" /><Field grp="storage" k="max_upload_mb" label="Max upload (MB)" /></div>
          <div className="card-copper rounded-xl p-6 space-y-4"><h3 className="font-display font-bold uppercase">Security</h3><Field grp="security" k="require_member_for_trackids" label="Require member for Track IDs" type="bool" /><Field grp="security" k="brute_force_lockout" label="Brute-force lockout" type="bool" /></div>
        </>
      )}
      <button data-testid="save-settings" onClick={save} className="copper-btn rounded-lg px-6 py-3 mono-label">SAVE SETTINGS</button>
    </div>
  );
}

function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/superadmin/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <Spinner />;
  const max = Math.max(...Object.values(a.genre_breakdown || { x: 1 }), 1);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Total Views" value={formatViews(a.total_views)} icon={BarChart3} testid="an-views" />
        <StatCard label="Published" value={a.published} testid="an-pub" />
        <StatCard label="Members" value={a.members} testid="an-members" />
      </div>
      <div className="card-copper rounded-xl p-6">
        <h3 className="font-display font-bold uppercase mb-4">Genre Breakdown</h3>
        <div className="space-y-2">
          {Object.entries(a.genre_breakdown || {}).sort((x, y) => y[1] - x[1]).map(([g, c]) => (
            <div key={g} className="flex items-center gap-3"><span className="mono-label text-[#A1A1AA] w-32 truncate">{g}</span><div className="flex-1 h-3 bg-[#1A1A22] rounded overflow-hidden"><div className="h-full bg-[#E8552A]" style={{ width: `${(c / max) * 100}%` }} /></div><span className="mono-label text-[#71717A]">{c}</span></div>
          ))}
        </div>
      </div>
      <div className="card-copper rounded-xl p-6">
        <h3 className="font-display font-bold uppercase mb-4">Top Recordings</h3>
        {a.top_recordings.map((r, i) => (<div key={r.id} className="flex items-center justify-between py-2 border-b border-[#1A1A22] last:border-0"><span className="text-sm"><span className="text-[#E8552A] mono-label mr-2">#{i + 1}</span>{r.title}</span><span className="mono-label text-[#71717A]">{formatViews(r.views)}</span></div>))}
      </div>
    </div>
  );
}

function Alerts() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/notifications").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return <Spinner />;
  if (items.length === 0) return <EmptyState title="No system alerts" icon={Bell} />;
  const color = { success: "#10B981", warning: "#FF9F1C", error: "#ef4444", info: "#E8552A" };
  return (
    <div className="space-y-3">
      {items.map((n) => (
        <div key={n.id} className="card-copper rounded-xl p-4 flex items-start gap-3">
          <span className="w-2 h-2 rounded-full mt-2" style={{ background: color[n.level] || "#E8552A" }} />
          <div><p className="font-display font-semibold text-sm">{n.title}</p><p className="text-sm text-[#A1A1AA]">{n.message}</p><p className="mono-label text-[#71717A] mt-1">{new Date(n.created_at).toLocaleString()} · {n.category}</p></div>
        </div>
      ))}
    </div>
  );
}
